import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Input,
  Textarea,
  Button,
  Select,
  Checkbox,
  VStack,
  HStack,
  Image,
  Container,
  Heading,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftElement,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useToast
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import ConversationMarkdown from '../components/TaskViewer/ConversationMarkdown';

import Header from "../components/Header";
import UserMenu from "../components/NavBar/Components/UserMenu";
import LangSelectV2 from "../components/NavBar/LangSelectV2";
import { SupportService, CreateTicketRequest } from "../services/supportService";
import FeedbackSuccess from "../components/FeedbackSuccess";

const Support: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth0();
  const toast = useToast();
  
  // 表单状态
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    questionType: '',
    information: '',
    agreeToContact: false
  });

  // 提交状态
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 成功状态 - 控制显示表单还是成功组件
  const [isSuccess, setIsSuccess] = useState(false);
  const [successData, setSuccessData] = useState<{ticket?: string; email?: string}>({});

  // FAQ搜索状态
  const [searchQuery, setSearchQuery] = useState('');

  // FAQ数据现在从国际化文件中获取
  const faqKeys = [
    'faq-question-1',
    'faq-question-2', 
    'faq-question-3',
    'faq-question-4',
    'faq-question-5',
    'faq-question-6',
    'faq-question-7',
    'faq-question-8',
    'faq-question-9',
    'faq-question-10',
    'faq-question-11'
  ];

  const faqData = faqKeys.map(key => ({
    question: t(key),
    answer: t(key.replace('question', 'answer'))
  }));

  // 过滤FAQ
  const filteredFAQ = faqData.filter(item => {
    return item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
           item.answer.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    // 按顺序验证必填字段
    if (!formData.firstName) {
      toast({
        title: "Please fill in your first name",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.lastName) {
      toast({
        title: "Please fill in your last name",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.email) {
      toast({
        title: "Please fill in your email address",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // 验证邮箱格式
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Please enter a valid email address",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.questionType) {
      toast({
        title: "Please select a question type",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.information.trim()) {
      toast({
        title: "Please provide information about your question",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // 必须勾选同意被联系，否则不允许提交
    if (!formData.agreeToContact) {
      toast({
        title: "Please agree to be contacted via email",
        description: "To proceed, please check the consent box.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // 准备API请求数据
      const ticketData: CreateTicketRequest = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        contact_email: formData.email,
        question_type: formData.questionType,
        information: formData.information
      };

      // 调用API
      const response = await SupportService.createTicket(ticketData);

      if (response.success) {
        // 成功创建ticket
        // toast({
        //   title: "Ticket created successfully!",
        //   description: `Your ticket number is ${response.ticket_number}`,
        //   status: "success",
        //   duration: 5000,
        //   isClosable: true,
        // });

        // 设置成功数据并显示成功组件
        setSuccessData({
          ticket: response.ticket_number,
          email: response.contact_email
        });
        setIsSuccess(true);

        // 重置表单
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          questionType: '',
          information: '',
          agreeToContact: false
        });
      } else {
        // 处理验证错误
        if (response.errors) {
          // 显示具体的字段错误
          Object.entries(response.errors).forEach(([field, messages]) => {
            toast({
              title: `${field}: ${messages.join(', ')}`,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          });
        } else {
          // 显示通用错误消息
          toast({
            title: "Failed to create ticket",
            description: response.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast({
        title: "Network Error",
        description: "Failed to submit your request. Please check your connection and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 处理返回到表单
  const handleBack = () => {
    setIsSuccess(false);
    setSuccessData({});
  };

  // 如果显示成功组件
  if (isSuccess) {
    return (
      <Box h="100vh" overflowY={'auto'} className='main-bg' position="relative">
        {/* 顶部语言与用户菜单 */}
        <Box position="fixed" top={0} w={'100%'} px={'40px'} zIndex={1000}>
          <Header />
        </Box>
        <Container maxW="1400px" mx="auto" px={8} py={12} position="relative" zIndex={1}>
          <FeedbackSuccess 
            ticket={successData.ticket}
            email={successData.email}
            onBack={handleBack}
          />
        </Container>
      </Box>
    );
  }

  return (
    <Box h="100vh" overflowY={'auto'}  className='main-bg'
        position="relative">

      {/* 顶部语言与用户菜单 */}
      <Box position="fixed" top={0} w={'100%'} px={'40px'} zIndex={1000}>
        {/* 语言选择和用户菜单 */}
        <Flex flexDir={"row"} h={"90px"} justify="space-between" align="center" position="absolute" top={0} right={'40px'}>
          <HStack>
            <Box minWidth={"120px"}>
              <LangSelectV2 />
            </Box>
            {isAuthenticated && user && <UserMenu user={user} />}
          </HStack>
        </Flex>
      </Box>
      <Container maxW="1400px" mx="auto" px={8} py={12} pt="100px" position="relative" zIndex={1}>
        {/* 主要内容区域 - 两列布局 */}
        <Flex direction={{ base: "column", lg: "row" }} gap={24} align="flex-start">
          {/* 左侧 - FAQ部分 */}
          <Box flex={1} maxW={{ base: "100%", lg: "600px" }}>
            <VStack align="stretch">
              <Heading as="h2" size="xl" color="gray.800" fontWeight="bold">
                Frequently asked questions
              </Heading>
              
              {/* 搜索框 */}
               <Box mb={2}>
                 <InputGroup size="md">
                   <InputLeftElement pointerEvents="none">
                     <Box
                       as="img"
                       src="/static/editor/search-normal.svg"
                       alt="Search"
                       w={5}
                       h={5}
                     />
                   </InputLeftElement>
                   <Input
                     placeholder="Search frequently asked questions"
                     value={searchQuery}
                     onChange={(e) => setSearchQuery(e.target.value)}
                     bg="white"
                     border="1px solid #E2E8F0"
                     borderRadius="24px"
                     py={3}
                     _placeholder={{ color: '#A0AEC0' }}
                     _focus={{
                       border: "1px solid #E2E8F0",
                       boxShadow: "none"
                     }}
                   />
                 </InputGroup>
               </Box>

              {/* FAQ列表 */}
              <Box
                bg="white"
               
                borderRadius="12px"
                overflow="hidden"
              >
                <Accordion allowMultiple>
                  {filteredFAQ.map((item, index) => (
                    <AccordionItem key={index} border="none">
                      <AccordionButton
                        py={4}
                        px={6}
                        _hover={{ bg: 'gray.50' }}
                        borderBottom={index < filteredFAQ.length - 1 ? '1px solid' : 'none'}
                        borderColor="gray.200"
                      >
                        <Box flex="1" textAlign="left">
                          <Text fontWeight="bold" color="#333" fontSize="20px" mb={0}>
                            {t(item.question)}
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel px={6} py={4} bg="gray.50">
                        <ConversationMarkdown content={item.answer} />
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Box>

              {/* 无搜索结果 */}
              {searchQuery && filteredFAQ.length === 0 && (
                <Box textAlign="center" py={8}>
                  <Text color="gray.500">
                    No questions found matching "{searchQuery}"
                  </Text>
                </Box>
              )}
            </VStack>
          </Box>

          {/* 右侧 - 联系表单部分 */}
          <Box flex={1} maxW={{ base: "100%", lg: "600px" }}>
            {/* Logo部分 */}
            <Box
              onClick={() => {
                navigate("/edit");
              }}
              cursor={"pointer"}
              mb={4}
            >
              <Image
                w={"144px"}
                h={"40px"}
                objectFit="contain"
                src={"/static/doenba-logo-blue.png"}
              />
            </Box>
        
            {/* Logo和标题部分 */}
            <VStack spacing={6} mb={8} align="left">
                <Heading mb={0} as="h1" size="xl" color="gray.800" fontWeight="bold">
                  How can we help?
                </Heading>
                <Text fontSize="lg" color="#707F8F"  mb={0}>
                  No matter your plan, needs, or goals, we're here to listen and support you.
                </Text>
            </VStack>

            {/* 联系表单 */}
            <Box w="full">
              <VStack spacing={6}>
                <HStack spacing={4} w="full">
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                      First Name
                    </FormLabel>
                    <Input
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      bg="white"
                      border="1px solid"
                      borderColor="#1D8AFF"
                      borderRadius="6px"
                      w="240px"
                      _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                      Last Name
                    </FormLabel>
                    <Input
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      bg="white"
                      border="1px solid"
                      borderColor="#1D8AFF"
                      borderRadius="6px"
                      w="240px"
                      _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                    />
                  </FormControl>
                </HStack>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                    Contact Email
                  </FormLabel>
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    bg="white"
                    border="1px solid"
                    borderColor="#1D8AFF"
                    borderRadius="6px"
                    w="240px"
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                  />
                  <HStack mt={3} spacing={3} alignSelf="flex-start">
                  <Checkbox
                    isChecked={formData.agreeToContact}
                    onChange={(e) => handleInputChange('agreeToContact', e.target.checked)}
                    colorScheme="blue"
                    size="md"
                     borderColor="#1D8AFF"
                      borderRadius="2px"
                  />
                  <Text mb={0} fontSize="sm" color="gray.700">
                    I agree to Doenba Service Team contact me via email
                  </Text>
                </HStack>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                    Question Type
                  </FormLabel>
                  <Select
                    value={formData.questionType}
                    onChange={(e) => handleInputChange('questionType', e.target.value)}
                    border="1px solid"
                    bg="white"
                    borderColor="#1D8AFF"
                    borderRadius="6px"
                    w="240px"
                    icon={<Box w={'26px'} p={'2px'}>
                    <Image
                        w={'24px'}
                        objectFit="cover"
                        src="/static/arrow-down.png"
                        alt="Doenba"
                    />
                </Box>}
                  >
                    <option value="" disabled hidden>Select question type</option>
                    <option value="bug">Bug Report</option>
                    <option value="billing">Billing Question</option>
                    <option value="technical">Technical Support</option>
                    <option value="other">Other</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                    Information
                  </FormLabel>
                  <Textarea
                    placeholder="Enter Information"
                    value={formData.information}
                    onChange={(e) => handleInputChange('information', e.target.value)}
                    bg="white"
                    border="1px solid"
                    borderColor="#1D8AFF"
                    borderRadius="6px"
                    rows={8}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                  />
                </FormControl>

                

               <FormControl>
              
                  <Button
                    width="139px"
                    h="48px"
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    loadingText="Submitting..."
                    color="white"
                    size="lg"
                    bg={'#1D8AFF'}
                    borderRadius="12px"
                    _hover={{ bg: 'blue.600' }}
                    _active={{ bg: 'blue.700' }}
                    py={6}
                    fontSize="md"
                    fontWeight="medium"
                    float={'right'}
                     variant={'solid'}
                  colorScheme='brand'
                  >
                    Submit
                  </Button>
                </FormControl>
              </VStack>
            </Box>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default Support;